import React, { useState } from 'react';
import api from '../api';

export default function StreamKey({ streamInfo, onRegenerate }) {
  const [copied, setCopied] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [regen, setRegen] = useState(false);
  const [recording, setRecording] = useState(streamInfo?.recordingEnabled || false);
  const [savingRec, setSavingRec] = useState(false);

  React.useEffect(() => {
    if (streamInfo?.recordingEnabled !== undefined) {
      setRecording(streamInfo.recordingEnabled);
    }
  }, [streamInfo?.recordingEnabled]);

  const toggleRecording = async () => {
    const newVal = !recording;
    setRecording(newVal);
    setSavingRec(true);
    try {
      await api.patch('/stream/key', { recording_enabled: newVal });
    } catch {
      setRecording(!newVal); // revert on error
    } finally {
      setSavingRec(false);
    }
  };

  const copy = async (text, id) => {
    await navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(''), 2000);
  };

  const handleRegen = async () => {
    if (!window.confirm('Regenerate stream key? Your encoder setup will need updating.')) return;
    setRegen(true);
    try { await api.post('/stream/key/regenerate'); onRegenerate(); }
    finally { setRegen(false); }
  };

  const s = {
    card: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '22px 24px', marginBottom: '16px' },
    title: { fontSize: '11px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '14px' },
    grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '12px' },
    row: { display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' },
    label: { fontSize: '11px', fontWeight: '600', color: 'var(--text-muted)', marginBottom: '5px', display: 'flex', alignItems: 'center', gap: '6px' },
    badge: (color) => ({ fontSize: '9px', fontWeight: '800', padding: '2px 5px', borderRadius: '4px', background: `${color}20`, color, border: `1px solid ${color}40`, letterSpacing: '0.5px' }),
    mono: { fontFamily: 'var(--mono)', fontSize: '12px', background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: '8px', padding: '8px 12px', flex: 1, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
    copyBtn: (active) => ({ flexShrink: 0, background: 'var(--surface2)', border: `1px solid ${active ? 'var(--signal)' : 'var(--border)'}`, color: active ? 'var(--signal)' : 'var(--text-muted)', borderRadius: '7px', padding: '8px 12px', fontSize: '12px', fontWeight: '600', minWidth: '58px', cursor: 'pointer', transition: 'all 0.2s' }),
    actions: { display: 'flex', gap: '8px', marginTop: '4px' },
    smallBtn: { fontSize: '12px', color: 'var(--text-muted)', background: 'transparent', border: '1px solid var(--border)', borderRadius: '7px', padding: '5px 11px', cursor: 'pointer' },
    divider: { borderTop: '1px solid var(--border)', margin: '14px 0' },
  };

  if (!streamInfo) return (
    <div style={s.card}><div style={s.title}>Loading connection details…</div></div>
  );

  const maskedKey = '••••••••••••' + streamInfo.streamKey.slice(-6);

  const CopyRow = ({ label, value, id, badge, badgeColor }) => (
    <div style={{ marginBottom: '12px' }}>
      <div style={s.label}>
        {label}
        {badge && <span style={s.badge(badgeColor || 'var(--signal)')}>{badge}</span>}
      </div>
      <div style={s.row}>
        <div style={s.mono} title={value}>{value}</div>
        <button style={s.copyBtn(copied === id)} onClick={() => copy(value, id)}>
          {copied === id ? '✓' : 'Copy'}
        </button>
      </div>
    </div>
  );

  return (
    <div style={s.card}>
      <div style={s.title}>Connection Details</div>

      {/* Stream Key */}
      <div style={{ marginBottom: '12px' }}>
        <div style={s.label}>Stream Key</div>
        <div style={s.row}>
          <div style={s.mono}>{showKey ? streamInfo.streamKey : maskedKey}</div>
          <button style={s.copyBtn(copied === 'key')} onClick={() => copy(streamInfo.streamKey, 'key')}>
            {copied === 'key' ? '✓' : 'Copy'}
          </button>
        </div>
        <div style={s.actions}>
          <button style={s.smallBtn} onClick={() => setShowKey(!showKey)}>
            {showKey ? '🙈 Hide' : '👁 Show'}
          </button>
          <button style={s.smallBtn} onClick={handleRegen} disabled={regen}>
            {regen ? 'Regenerating…' : '↻ Regenerate'}
          </button>
        </div>
      </div>

      {/* Recording toggle */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '10px 14px', background: recording ? 'rgba(255,77,77,0.06)' : 'var(--surface2)',
        border: `1px solid ${recording ? 'rgba(255,77,77,0.2)' : 'var(--border)'}`,
        borderRadius: '8px', marginTop: '10px', cursor: 'pointer', transition: 'all 0.2s',
      }} onClick={toggleRecording}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{
            width: '8px', height: '8px', borderRadius: '50%',
            background: recording ? '#ff4d4d' : 'var(--text-muted)',
            boxShadow: recording ? '0 0 6px #ff4d4d' : 'none',
            transition: 'all 0.2s', flexShrink: 0,
          }} />
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: recording ? '#ff4d4d' : 'var(--text-muted)' }}>
              {recording ? '🔴 Recording enabled' : 'Recording disabled'}
            </div>
            <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '1px' }}>
              {recording ? 'Every stream will be recorded automatically' : 'Click to enable recording for all your streams'}
            </div>
          </div>
        </div>
        <div style={{
          width: '36px', height: '20px', borderRadius: '10px',
          background: recording ? '#ff4d4d' : 'var(--border)',
          position: 'relative', flexShrink: 0, transition: 'background 0.2s',
          opacity: savingRec ? 0.6 : 1,
        }}>
          <div style={{
            position: 'absolute', top: '3px',
            left: recording ? '19px' : '3px',
            width: '14px', height: '14px', borderRadius: '50%',
            background: '#fff', transition: 'left 0.2s',
          }} />
        </div>
      </div>

      <div style={s.divider} />

      <div style={s.grid}>
        <div>
          <CopyRow label="RTMP (OBS / Default)" value={streamInfo.ingestUrl} id="rtmp" badge="RTMP" badgeColor="#f59e0b" />
          <CopyRow label="RTMPS (Encrypted)" value={streamInfo.rtmpsUrl} id="rtmps" badge="TLS" badgeColor="var(--signal)" />
        </div>
        <div>
          <CopyRow label="SRT (Low Latency)" value={streamInfo.srtUrl} id="srt" badge="SRT" badgeColor="#9146ff" />
          <CopyRow label="HLS Playback URL" value={streamInfo.hlsUrl} id="hls" badge="HLS" badgeColor="#1877f2" />
        </div>
      </div>
    </div>
  );
}
